"use client";
import React, { useEffect, useState, useRef } from "react";
import {
  blockStyles,
  blockTypes,
  icons,
  timeOfDayOptions,
  Block,
} from "../types/screenplay-editor.types";
import { useScreenplayEditor } from "../hook/use-screenplay-editor";
import { comparePositions } from "../service/fractional-indexing";
import {
  LANGUAGE_CONFIGS,
  LanguageType,
} from "../service/multilingual-input.service";
import {
  ArrowRight,
  ChevronRight,
  User,
  Cog,
  Save,
  FileDown,
  Upload,
  RefreshCcw,
  Lock,
  Unlock,
  Loader2,
  Sparkles,
  Check,
  X,
  Languages,
  Keyboard,
  Film,
} from "lucide-react";
import { PhoneticSuggestions } from "../components/input/PhoneticSuggestions";

import { PdfImporter } from "../components/PdfImporter";
import { PhoneticGuide } from "../components/PhoneticGuide";

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  shortcut?: string;
  align?: "center" | "left" | "right";
}

function Tooltip({
  children,
  content,
  shortcut,
  align = "center",
}: TooltipProps) {
  const alignClasses = {
    center: "left-1/2 -translate-x-1/2 items-center",
    left: "left-0 items-start",
    right: "right-0 items-end",
  };

  const arrowClasses = {
    center: "",
    left: "left-3.5",
    right: "right-3.5",
  };

  return (
    <div className="group relative flex items-center justify-center">
      {children}
      <div
        className={`absolute top-[calc(100%+8px)] scale-95 opacity-0 pointer-events-none group-hover:scale-100 group-hover:opacity-100 transition-all duration-150 ease-out z-[100] flex flex-col ${alignClasses[align]}`}
      >
        <div
          className={`w-2 h-2 rotate-45 bg-blumine-950 border-l border-t border-white/10 -mb-1 shadow-sm ${align === "center" ? "" : "relative"} ${arrowClasses[align]}`}
        />
        <div className="bg-blumine-950/95 text-white text-[11px] font-medium px-2.5 py-1.5 rounded-lg shadow-xl border border-white/10 backdrop-blur-sm whitespace-nowrap flex items-center gap-1.5">
          <span>{content}</span>
          {shortcut && (
            <kbd className="bg-blumine-800 text-[10px] text-blumine-300 px-1.5 py-0.5 rounded border border-zinc-700 font-sans font-semibold">
              {shortcut}
            </kbd>
          )}
        </div>
      </div>
    </div>
  );
}

type ScreenplayEditorViewProps = ReturnType<typeof useScreenplayEditor> & {
  showPdfImport?: boolean;
  showSaveButton?: boolean;
  showSyncButton?: boolean;
  isLocked?: boolean;
  onToggleLock?: () => void;
  onSave?: () => void;
  onSaveAsPdf?: () => void;
  onSaveAsSbx?: () => void;
  onSyncWithCloud?: () => void;
  handleSceneNumberChange: (blockId: string, value: string) => void;
  handleEnhance: (block: Block) => void;
  handleApproveEnhance: () => void;
  handleRejectEnhance: () => void;
  enhancingBlockId: string | null;
  enhancementSuggestion: string | null;
  isEnhancing: boolean;
  savePopOverLeft?: string;
  currentLanguage: LanguageType;
  setCurrentLanguage: (lang: LanguageType) => void;
  phoneticSuggestions: string[];
  handleSelectPhoneticSuggestion: (suggestion: string) => void;
};
export function ScreenplayEditorView({
  blocks,
  refs,
  focusedBlockId,
  showSuggestions,
  showExtensionSuggestions,
  characterExtensions,
  locations,
  characters,
  showPdfImport = false,
  showSaveButton = false,
  showSyncButton = false,
  isLocked = false,
  onToggleLock,
  handleBlockTextChange,
  handleSceneTypeChange,
  handleTimeOfDayChange,
  handleBlockTypeChange,
  handleSelectCharacterExtension,
  handleKeyDown,
  handleFocus,
  handleBlur,
  handleScriptImport,
  onSave,
  onSaveAsPdf,
  onSyncWithCloud,
  handleSceneNumberChange,
  handleEnhance,
  handleApproveEnhance,
  handleRejectEnhance,
  enhancingBlockId,
  enhancementSuggestion,
  isEnhancing,
  showUnsavedPopover,
  syncScreenplay,
  ignoreChanges,
  isLoading = false,
  savePopOverLeft = "53%",
  currentLanguage,
  setCurrentLanguage,
  phoneticSuggestions,
  handleSelectPhoneticSuggestion,
  autosaveStatus,
}: ScreenplayEditorViewProps) {
  const sortedBlocks = React.useMemo(() => {
    return [...blocks].sort((a, b) => comparePositions(a.position, b.position));
  }, [blocks]);

  const [isRulesOpen, setIsRulesOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isPhoneticGuideOpen, setIsPhoneticGuideOpen] = useState(false);
  const rulesRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  const COURIER_STACK = "'Courier Prime', 'Courier', monospace";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        rulesRef.current &&
        !rulesRef.current.contains(event.target as Node)
      ) {
        setIsRulesOpen(false);
      }
      if (
        languageRef.current &&
        !languageRef.current.contains(event.target as Node)
      ) {
        setIsLanguageOpen(false);
      }
    };

    if (isRulesOpen || isLanguageOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isRulesOpen, isLanguageOpen]);

  useEffect(() => {
    const fontId = "google-font-courier-prime";
    const styleId = "screenplay-editor-force-v4";

    // 1. Inject Google Font
    if (!document.getElementById(fontId)) {
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap";
      document.head.appendChild(link);
    }

    // 2. Inject high-specificity CSS (Safe textContent approach)
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        /* We target by the data-attribute to ensure the highest specificity possible */
        [data-screenplay-editor] *, 
        [data-screenplay-editor] div, 
        [data-screenplay-editor] span, 
        [data-screenplay-editor] [contenteditable="true"] {
          font-family: ${COURIER_STACK} !important;
          -webkit-font-smoothing: antialiased;
        }
      `;
      document.head.appendChild(style);
    }
  }, [COURIER_STACK]);

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-blumine-500/40 backdrop-blur-md p-6">
          <style>{`
            @keyframes gpu-spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .gpu-spinner-container {
              transform: translate3d(0,0,0);
              will-change: transform;
            }
            .gpu-spinner {
              width: 32px;
              height: 32px;
              border: 3.5px solid rgba(255, 255, 255, 0.25);
              border-top-color: #ffffff;
              border-radius: 50%;
              animation: gpu-spin 0.85s linear infinite;
              will-change: transform;
              transform: translate3d(0,0,0);
            }
          `}</style>
          <div className="h-full w-full border-2 border-white border-dashed rounded-[2.5rem] flex items-center justify-center gpu-spinner-container">
            <div className="flex flex-col items-center">
              <div className="gpu-spinner" />
              <p className="text-white text-lg font-semibold mt-4">
                Preparing Script...
              </p>
              <p className="text-white text-xs font-medium mt-2">
                Please wait while we prepare your screenplay.
              </p>
              <span className="text-white/80 text-xs mt-2">
                Status: Arranging data...
              </span>
            </div>
          </div>
        </div>
      )}
      <div className="sticky top-0 left-0 right-0 z-50 w-full bg-zinc-50/95 backdrop-blur-md border-b border-zinc-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.01)] flex flex-wrap lg:flex-nowrap items-center justify-between px-3 sm:px-6 py-2 mb-8 select-none transition-all gap-y-2">
        {/* Left Section: Screenplay Branding */}
        <div className="flex items-center gap-3 w-auto shrink-0 order-1">
          <span className="text-[11px] font-bold tracking-wider text-blumine-400/80 uppercase">
            Script Editor
          </span>
          {autosaveStatus === "saved" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100/50 animate-in fade-in duration-300">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Script synced
              </span>
            </div>
          )}
          {autosaveStatus === "saving" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100/50 animate-in fade-in duration-300">
              <Loader2 className="w-2.5 h-2.5 animate-spin" />
              <span className="text-[10px] font-semibold tracking-wide">
                Saving...
              </span>
            </div>
          )}
          {autosaveStatus === "typing" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-100/50 animate-in fade-in duration-300">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Drafting...
              </span>
            </div>
          )}
          {autosaveStatus === "error" && (
            <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-100/50 animate-in fade-in duration-300">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              <span className="text-[10px] font-semibold tracking-wide">
                Error saving
              </span>
            </div>
          )}
        </div>

        {/* Center Section: Centered Format Buttons (Direct row layout as requested, scales dynamically, wraps to own line on mobile to prevent squishing) */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 basis-full lg:basis-auto flex-initial min-w-0 py-1 lg:py-0.5 order-3 lg:order-2">
          {blockTypes.map((type) => {
            const selected =
              blocks.find((b) => b.id === focusedBlockId)?.type === type;

            return (
              <Tooltip
                key={type}
                content={blockStyles[type].label}
                shortcut="Ctrl + ↑/↓"
              >
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={() => handleBlockTypeChange(type)}
                  className={`w-8 h-8 sm:w-9 sm:h-9 flex shrink-0 items-center justify-center rounded-lg transition-all duration-150 ease-out active:scale-95 whitespace-nowrap ${
                    selected
                      ? "bg-blumine-200/60 text-blumine-800 shadow-sm border border-blumine-200/50"
                      : "text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700"
                  } ${isLocked ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <div
                    className={`flex items-center justify-center transition-all duration-150 ${selected ? "scale-105 opacity-100" : "opacity-80 hover:opacity-100"}`}
                  >
                    {icons[type]}
                  </div>
                </button>
              </Tooltip>
            );
          })}
        </div>

        {/* Right Section: Utilities Styled in Monochromatic blumine - Dynamically fits container */}
        <div className="flex items-center justify-end gap-1 sm:gap-1.5 w-auto lg:w-[220px] shrink-0 order-2 lg:order-3">
          {onSave && showSaveButton && !isLocked && (
            <Tooltip content="Save changes" align="right">
              <button
                onClick={onSave}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700 transition active:scale-95"
              >
                <Save
                  className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                  strokeWidth={1.5}
                />
              </button>
            </Tooltip>
          )}

          {onToggleLock && (
            <Tooltip
              content={isLocked ? "Unlock Screenplay" : "Lock Screenplay"}
              align="right"
            >
              <button
                onClick={onToggleLock}
                className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg transition active:scale-95 ${
                  isLocked
                    ? "text-rose-500 hover:bg-rose-50 hover:text-rose-700 bg-rose-50/50"
                    : "text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700"
                }`}
              >
                {isLocked ? (
                  <Lock
                    className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                    strokeWidth={1.5}
                  />
                ) : (
                  <Unlock
                    className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                    strokeWidth={1.5}
                  />
                )}
              </button>
            </Tooltip>
          )}

          <div ref={rulesRef} className="relative flex items-center">
            <Tooltip content="Settings & Actions" align="right">
              <button
                onClick={() => setIsRulesOpen(!isRulesOpen)}
                className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg transition-all duration-200 active:scale-95 ${isRulesOpen ? "bg-blumine-200/60 text-blumine-800 shadow-sm border border-blumine-200/50" : "text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700"}`}
              >
                <Cog
                  className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                  strokeWidth={1.5}
                />
              </button>
            </Tooltip>

            {isRulesOpen && (
              <div className="absolute right-0 top-[calc(100%+14px)] w-[360px] max-w-[90vw] p-4 rounded-2xl bg-white border border-blumine-200 shadow-2xl backdrop-blur-xl origin-top-right animate-in fade-in zoom-in-95 z-50">
                <div className="absolute -top-1.5 right-3.5 w-3 h-3 rotate-45 bg-white border-l border-t border-blumine-200" />

                <div className="flex items-start justify-between mb-4 px-1">
                  <div className="flex gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-b from-white to-blumine-50/50 text-blumine-600 border border-blumine-200 shadow-inner">
                      <Cog className="w-4 h-4" strokeWidth={1.5} />
                    </div>

                    <div>
                      <h4 className="text-[15px] font-semibold text-blumine-800 leading-tight">
                        Settings & Actions
                      </h4>
                      <p className="text-xs text-blumine-400 mt-1">
                        Screenplay controls & keyboard shortcuts
                      </p>
                    </div>
                  </div>
                </div>

                {/* --- Document Actions Section --- */}
                <div className="space-y-2 mb-4">
                  <div className="px-1.5 mb-1">
                    <span className="text-[11px] font-bold text-blumine-400 uppercase tracking-wider">
                      Document Actions
                    </span>
                  </div>

                  {/* Import PDF Script */}
                  {showPdfImport && (
                    <PdfImporter
                      disabled={isLocked}
                      onScriptImported={(title, content, preParsedBlocks) => {
                        handleScriptImport(title, content, preParsedBlocks);
                        setIsRulesOpen(false); // Close the settings popover after importing
                      }}
                      className="w-full text-left"
                    >
                      <div
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border border-blumine-100 bg-gradient-to-b from-white to-blumine-50/10 hover:from-blumine-50/40 hover:to-blumine-50/60 hover:border-blumine-200 hover:shadow-sm active:scale-[0.98] transition-all duration-200 cursor-pointer ${isLocked ? "opacity-60 cursor-not-allowed pointer-events-none" : ""}`}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blumine-50 text-blumine-600 border border-blumine-100 shrink-0">
                          <Upload className="w-4 h-4" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-semibold text-blumine-800 leading-none">
                            Import PDF
                          </div>
                          <div className="text-[11px] text-blumine-400 mt-1">
                            {isLocked
                              ? "Unlock screenplay to import files"
                              : "Load screenplay from external file"}
                          </div>
                        </div>
                      </div>
                    </PdfImporter>
                  )}

                  {/* Lock Screenplay */}
                  {onToggleLock && (
                    <button
                      onClick={() => {
                        onToggleLock();
                      }}
                      className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-blumine-100 bg-gradient-to-b from-white to-blumine-50/10 hover:from-blumine-50/40 hover:to-blumine-50/60 hover:border-blumine-200 hover:shadow-sm active:scale-[0.98] transition-all duration-200"
                    >
                      <div
                        className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 border ${
                          isLocked
                            ? "bg-rose-50 text-rose-500 border-rose-100/50"
                            : "bg-blumine-50 text-blumine-600 border-blumine-100/50"
                        }`}
                      >
                        {isLocked ? (
                          <Lock className="w-4 h-4" strokeWidth={1.5} />
                        ) : (
                          <Unlock className="w-4 h-4" strokeWidth={1.5} />
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-blumine-800 leading-none">
                          {isLocked ? "Unlock Screenplay" : "Lock Screenplay"}
                        </div>
                        <div className="text-[11px] text-blumine-400 mt-1">
                          {isLocked
                            ? "Enable editing controls"
                            : "Prevent accidental editing"}
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Export as PDF */}
                  {onSaveAsPdf && showSaveButton && (
                    <button
                      onClick={() => {
                        onSaveAsPdf();
                        setIsRulesOpen(false); // Close popover on save
                      }}
                      className="w-full text-left flex items-center gap-3 p-3 rounded-xl border border-blumine-100 bg-gradient-to-b from-white to-blumine-50/10 hover:from-blumine-50/40 hover:to-blumine-50/60 hover:border-blumine-200 hover:shadow-sm active:scale-[0.98] transition-all duration-200"
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blumine-50 text-blumine-600 border border-blumine-100 shrink-0">
                        <FileDown className="w-4 h-4" strokeWidth={1.5} />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-blumine-800 leading-none">
                          Save as PDF
                        </div>
                        <div className="text-[11px] text-blumine-400 mt-1">
                          Export screenplay to standard print PDF
                        </div>
                      </div>
                    </button>
                  )}
                </div>

                {/* Divider */}
                <div className="border-t border-blumine-100/60 my-4" />

                {/* --- Keyboard Shortcuts Section --- */}
                <div className="space-y-2">
                  <div className="px-1.5 mb-1">
                    <span className="text-[11px] font-bold text-blumine-400 uppercase tracking-wider">
                      Keyboard Shortcuts
                    </span>
                  </div>

                  {[
                    {
                      title: "New Block",
                      desc: "Insert the next section",
                      key: ["Enter"],
                    },
                    {
                      title: "Delete Block",
                      desc: "Remove selected section",
                      key: ["Backspace 🡠"],
                    },
                    {
                      title: "Change Type",
                      desc: "Cycle block styles",
                      key: ["Ctrl", "+", "↑/↓"],
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="flex items-center justify-between gap-4 p-2.5 rounded-xl bg-gradient-to-b from-white to-blumine-50/30 border border-blumine-100/80 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]"
                    >
                      <div className="flex flex-col min-w-0 text-left">
                        <span className="text-xs font-semibold text-blumine-800">
                          {item.title}
                        </span>
                        <span className="text-[10px] text-blumine-400 truncate mt-0.5">
                          {item.desc}
                        </span>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        {item.key.map((k, i) =>
                          k === "+" ? (
                            <span
                              key={i}
                              className="text-blumine-400 text-xs font-semibold"
                            >
                              +
                            </span>
                          ) : (
                            <kbd
                              key={i}
                              className="min-w-[28px] px-2 py-1 text-[11px] font-bold text-blumine-700 bg-gradient-to-b from-white to-blumine-100 border border-blumine-200 rounded-lg text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]"
                            >
                              {k}
                            </kbd>
                          ),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div
            ref={languageRef}
            className="relative flex items-center pl-1.5 border-l border-blumine-200"
          >
            <Tooltip
              content={`Input Language: ${LANGUAGE_CONFIGS[currentLanguage].name}`}
              align="right"
            >
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg transition-all duration-200 active:scale-95 ${isLanguageOpen ? "bg-blumine-200/60 text-blumine-800 border border-blumine-200 shadow-sm" : "text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700"}`}
              >
                <Languages
                  className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                  strokeWidth={1.5}
                />
              </button>
            </Tooltip>

            {isLanguageOpen && (
              <div className="absolute right-0 top-[calc(100%+14px)] w-[400px] max-w-[90vw] p-3 rounded-2xl bg-white border border-blumine-200 shadow-xl backdrop-blur-xl origin-top-right animate-in fade-in zoom-in-95 z-50">
                <div className="absolute -top-1.5 right-3.5 w-3 h-3 rotate-45 bg-white border-l border-t border-blumine-200" />

                <div className="px-2 mb-3">
                  <h4 className="text-[13px] font-bold text-blumine-800 uppercase tracking-wider">
                    Select Input Language
                  </h4>
                  <p className="text-[11px] text-blumine-400 mt-0.5">
                    Direct keyboard mapping (InScript logic)
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-1.5 max-h-[300px] overflow-y-auto pr-1 flex-1 custom-scrollbar">
                  {(Object.keys(LANGUAGE_CONFIGS) as LanguageType[]).map(
                    (lang) => (
                      <button
                        key={lang}
                        onClick={() => {
                          setCurrentLanguage(lang);
                          setIsLanguageOpen(false);
                        }}
                        className={`group flex items-center justify-between px-3.5 py-2.5 rounded-xl transition-all duration-200 ${currentLanguage === lang ? "bg-blumine-100/80 text-blumine-800 border border-blumine-200/50 shadow-sm" : "hover:bg-blumine-50 border border-transparent"}`}
                      >
                        <span
                          className={`text-sm font-medium ${currentLanguage === lang ? "text-blumine-800 font-semibold" : "text-blumine-500"}`}
                        >
                          {LANGUAGE_CONFIGS[lang].name}
                        </span>
                        {currentLanguage === lang && (
                          <div className="w-5 h-5 rounded-full bg-blumine-700 flex items-center justify-center text-white">
                            <Check className="w-3 h-3" strokeWidth={2} />
                          </div>
                        )}
                      </button>
                    ),
                  )}
                </div>
              </div>
            )}
          </div>

          {LANGUAGE_CONFIGS[currentLanguage].isPhonetic && (
            <Tooltip content="Phonetic Keyboard Rules" align="right">
              <button
                onClick={() => setIsPhoneticGuideOpen(true)}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg text-blumine-400 hover:bg-blumine-100 hover:text-blumine-700 transition active:scale-95 ml-1"
              >
                <Keyboard
                  className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                  strokeWidth={1.5}
                />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <PhoneticGuide
        language={currentLanguage}
        isOpen={isPhoneticGuideOpen}
        onClose={() => setIsPhoneticGuideOpen(false)}
      />

      <div className="flex flex-col gap-12 pb-24 w-full items-center justify-center px-4 sm:px-6 md:px-8">
        <div
          className="relative bg-[#fdfdfc] shadow-2xl shadow-zinc-300/60 border border-zinc-100 rounded-sm md:rounded-md pl-[1.5in] py-[1in] pr-[1in] flex flex-col w-[210mm] min-h-[297mm] shrink-0"
          style={{
            paddingLeft: "1.5in",
            paddingRight: "1in",
            paddingTop: "1in",
            paddingBottom: "1in",
            lineHeight: "1.2",
          }}
          data-screenplay-editor="true"
          dir={LANGUAGE_CONFIGS[currentLanguage].dir}
        >
          {sortedBlocks.map((block) => (
            <ScreenplayBlockItem
              key={block.id + "-" + block.type}
              block={block}
              isFocused={focusedBlockId === block.id}
              isLocked={isLocked}
              showSuggestions={showSuggestions}
              showExtensionSuggestions={showExtensionSuggestions}
              locations={locations}
              characters={characters}
              phoneticSuggestions={phoneticSuggestions}
              characterExtensions={characterExtensions}
              enhancingBlockId={enhancingBlockId}
              enhancementSuggestion={enhancementSuggestion}
              isEnhancing={isEnhancing}
              currentLanguage={currentLanguage}
              refs={refs}
              handleSceneNumberChange={handleSceneNumberChange}
              handleSceneTypeChange={handleSceneTypeChange}
              handleTimeOfDayChange={handleTimeOfDayChange}
              handleBlockTextChange={handleBlockTextChange}
              handleKeyDown={handleKeyDown}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              handleSelectPhoneticSuggestion={handleSelectPhoneticSuggestion}
              handleSelectCharacterExtension={handleSelectCharacterExtension}
              handleEnhance={handleEnhance}
              handleApproveEnhance={handleApproveEnhance}
              handleRejectEnhance={handleRejectEnhance}
            />
          ))}
        </div>
      </div>
    </>
  );
}

interface ScreenplayBlockItemProps {
  block: Block;
  isFocused: boolean;
  isLocked: boolean;
  showSuggestions: boolean;
  showExtensionSuggestions: boolean;
  locations: string[];
  characters: string[];
  phoneticSuggestions: string[];
  characterExtensions: string[];
  enhancingBlockId: string | null;
  enhancementSuggestion: string | null;
  isEnhancing: boolean;
  currentLanguage: LanguageType;
  refs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  handleSceneNumberChange: (blockId: string, value: string) => void;
  handleSceneTypeChange: (
    id: string,
    sceneType: "INT." | "EXT." | "INT/EXT.",
  ) => void;
  handleTimeOfDayChange: (id: string, time: any) => void;
  handleBlockTextChange: (id: string, text: string) => void;
  handleKeyDown: (
    e: React.KeyboardEvent<HTMLDivElement>,
    id: string,
    text: string,
  ) => void;
  handleFocus: (id: string) => void;
  handleBlur: (id: string) => void;
  handleSelectPhoneticSuggestion: (suggestion: string) => void;
  handleSelectCharacterExtension: (ext: string) => void;
  handleEnhance: (block: Block) => void;
  handleApproveEnhance: () => void;
  handleRejectEnhance: () => void;
}

const ScreenplayBlockItem = React.memo(
  function ScreenplayBlockItem({
    block,
    isFocused,
    isLocked,
    showSuggestions,
    showExtensionSuggestions,
    locations,
    characters,
    phoneticSuggestions,
    characterExtensions,
    enhancingBlockId,
    enhancementSuggestion,
    isEnhancing,
    currentLanguage,
    refs,
    handleSceneNumberChange,
    handleSceneTypeChange,
    handleTimeOfDayChange,
    handleBlockTextChange,
    handleKeyDown,
    handleFocus,
    handleBlur,
    handleSelectPhoneticSuggestion,
    handleSelectCharacterExtension,
    handleEnhance,
    handleApproveEnhance,
    handleRejectEnhance,
  }: ScreenplayBlockItemProps) {
    return (
      <div
        data-block-id={block.id}
        className={`relative rounded-sm transition-all duration-200 outline-none ${
          isFocused ? "bg-zinc-100/50" : "bg-transparent"
        }`}
        data-block-type={block.type}
      >
        {block.type === "SCENE_HEADING" ? (
          <>
            <div className="flex items-center gap-2 px-4 py-1 bg-transparent">
              <input
                className="absolute -left-16 top-2 w-12 text-right text-zinc-400 font-semibold select-none bg-transparent outline-none focus:ring-1 focus:ring-blue-400 rounded-sm"
                spellCheck={false}
                disabled={isLocked}
                value={block.sceneNumber || ""}
                onChange={(e) =>
                  handleSceneNumberChange(
                    block.id,
                    e.target.value.toUpperCase(),
                  )
                }
                onFocus={() => handleFocus(block.id)}
                onBlur={() => handleBlur(block.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Backspace") {
                    e.stopPropagation();
                  }
                }}
                aria-label="Scene Number"
              />
              <select
                className="rounded-md text-zinc-800 font-bold px-1.5 py-1 appearance-none bg-transparent hover:bg-zinc-200/50 outline-none cursor-pointer w-fit transition-colors"
                aria-label="Scene Type"
                disabled={isLocked}
                value={block.sceneType ?? "INT."}
                onChange={(e) =>
                  handleSceneTypeChange(block.id, e.target.value as any)
                }
                style={{
                  appearance: "none",
                }}
              >
                <option>INT.</option>
                <option>EXT.</option>
                <option>INT/EXT.</option>
              </select>
              <div
                ref={(el) => {
                  if (!el) return;
                  refs.current[block.id] = el;
                }}
                contentEditable={!isLocked}
                suppressContentEditableWarning
                aria-label={`Scene Heading: ${block.text}`}
                aria-haspopup="listbox"
                aria-expanded={
                  isFocused && showSuggestions && locations.length > 0
                }
                spellCheck={false}
                className="min-w-[3rem] py-1 outline-none text-base font-bold uppercase tracking-widest break-all bg-transparent"
                style={{
                  minWidth: "3rem",
                }}
                onInput={(e) =>
                  handleBlockTextChange(
                    block.id,
                    (e.target as HTMLDivElement).innerText,
                  )
                }
                onKeyDown={(e) => handleKeyDown(e, block.id, block.text)}
                onFocus={() => handleFocus(block.id)}
                onBlur={() => handleBlur(block.id)}
              ></div>
              <span className="text-zinc-400/80 font-bold">-</span>
              <select
                className="rounded-md text-zinc-800 font-bold px-1.5 py-1 appearance-none bg-transparent hover:bg-zinc-200/50 outline-none cursor-pointer transition-colors"
                aria-label="Time of Day"
                disabled={isLocked}
                value={block.timeOfDay ?? "DAY"}
                style={{
                  appearance: "none",
                }}
                onChange={(e) =>
                  handleTimeOfDayChange(block.id, e.target.value as any)
                }
              >
                {timeOfDayOptions.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            {isFocused && showSuggestions && locations.length > 0 && (
              <div
                role="listbox"
                id={`suggestions-${block.id}`}
                className="absolute top-[calc(100%+6px)] left-0 min-w-[240px] z-50 bg-white border border-slate-200/80 shadow-xl shadow-slate-200/40 rounded-xl py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-150"
              >
                <div className="max-h-60 overflow-y-auto custom-scrollbar">
                  {locations
                    .filter(
                      (loc) =>
                        loc.startsWith(block.text.toUpperCase()) &&
                        loc !== block.text.toUpperCase(),
                    )
                    .map((loc) => (
                      <div
                        role="option"
                        key={loc}
                        className="group flex items-center justify-between px-4 py-2.5 cursor-pointer transition-all duration-150 hover:bg-slate-50 active:bg-slate-100"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const element = refs.current[block.id];
                          if (element) {
                            element.innerText = loc;
                            handleBlockTextChange(block.id, loc);
                            element.focus();
                            const range = document.createRange();
                            const sel = window.getSelection();
                            range.selectNodeContents(element);
                            range.collapse(false);
                            sel?.removeAllRanges();
                            sel?.addRange(range);
                          }
                          handleBlur(block.id);
                        }}
                      >
                        <span className="text-[12px] font-semibold tracking-wide text-slate-600 uppercase line-clamp-1">
                          {loc}
                        </span>

                        <ArrowRight className="w-3.5 h-3.5 text-slate-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200" />
                      </div>
                    ))}
                </div>
              </div>
            )}
            {isFocused && phoneticSuggestions.length > 0 && (
              <PhoneticSuggestions
                suggestions={phoneticSuggestions}
                onSelect={handleSelectPhoneticSuggestion}
              />
            )}
          </>
        ) : (
          <>
            <div
              ref={(el) => {
                if (!el) return;
                refs.current[block.id] = el;
              }}
              contentEditable={!isLocked}
              suppressContentEditableWarning
              aria-label={`${blockStyles[block.type].label} text`}
              aria-multiline={
                block.type === "ACTION" || block.type === "DIALOGUE"
              }
              spellCheck={false}
              className={`block outline-none w-full min-h-[2.5rem] px-4 py-2 break-words ${
                blockStyles[block.type].className
              }`}
              onInput={(e) =>
                handleBlockTextChange(
                  block.id,
                  (e.target as HTMLDivElement).innerText,
                )
              }
              onKeyDown={(e) => handleKeyDown(e, block.id, block.text)}
              onFocus={() => handleFocus(block.id)}
              onBlur={() => handleBlur(block.id)}
              style={blockStyles[block.type].inputStyle}
            ></div>
            {isFocused &&
              block.type === "CHARACTER" &&
              showSuggestions &&
              characters.length > 0 && (
                <div
                  role="listbox"
                  id={`suggestions-${block.id}`}
                  className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-72 z-50 bg-white border border-slate-200 shadow-2xl shadow-slate-200/60 rounded-xl py-2 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                >
                  <div className="max-h-56 overflow-y-auto custom-scrollbar">
                    {characters
                      .filter(
                        (char) =>
                          char.startsWith(block.text.toUpperCase()) &&
                          char !== block.text.toUpperCase(),
                      )
                      .map((char) => (
                        <div
                          role="option"
                          key={char}
                          className="group flex items-center px-4 py-2.5 cursor-pointer transition-colors duration-150 hover:bg-slate-50 active:bg-slate-100"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            const element = refs.current[block.id];
                            if (element) {
                              element.innerText = char;
                              handleBlockTextChange(block.id, char);
                              element.focus();
                              const range = document.createRange();
                              const sel = window.getSelection();
                              range.selectNodeContents(element);
                              range.collapse(false);
                              sel?.removeAllRanges();
                              sel?.addRange(range);
                            }
                            handleBlur(block.id);
                          }}
                        >
                          <User className="w-3.5 h-3.5 text-slate-300 group-hover:text-blumine-500 transition-colors mr-3" />

                          <span className="flex-1 text-[11px] font-bold tracking-[0.1em] text-slate-600 uppercase text-left">
                            {char}
                          </span>

                          <ChevronRight className="w-3 h-3 text-slate-200 opacity-0 group-hover:opacity-100 transition-all -translate-x-1 group-hover:translate-x-0" />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            {isFocused &&
              block.type === "CHARACTER" &&
              showExtensionSuggestions &&
              characterExtensions && (
                <div
                  role="listbox"
                  id={`extension-suggestions-${block.id}`}
                  className="absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-72 z-50 bg-white border border-slate-200 shadow-2xl shadow-slate-200/60 rounded-xl py-2 overflow-hidden animate-in fade-in zoom-in-95 duration-200"
                >
                  <div className="max-h-56 overflow-y-auto custom-scrollbar">
                    {characterExtensions
                      .filter((ext) => {
                        const openParenIndex = block.text.lastIndexOf("(");
                        const query =
                          openParenIndex > -1
                            ? block.text
                                .substring(openParenIndex + 1)
                                .toUpperCase()
                            : "";
                        return ext.toUpperCase().includes(query);
                      })
                      .map((ext) => (
                        <div
                          role="option"
                          key={ext}
                          className="group flex items-center px-4 py-2.5 cursor-pointer transition-colors duration-150 hover:bg-slate-50 active:bg-slate-100"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelectCharacterExtension(ext);
                          }}
                        >
                          <span className="flex-1 text-[11px] font-bold tracking-[0.1em] text-slate-600 uppercase text-left">
                            {ext}
                          </span>
                          <ChevronRight className="w-3 h-3 text-slate-200 opacity-0 group-hover:opacity-100 transition-all -translate-x-1 group-hover:translate-x-0" />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            {isFocused &&
              !enhancingBlockId &&
              (block.type === "ACTION" || block.type === "DIALOGUE") && (
                <button
                  onClick={() => handleEnhance(block)}
                  className="absolute -right-12 top-1/2 -translate-y-1/2 flex items-center justify-center w-8 h-8 rounded-xl border border-blumine-200/50 bg-white text-blumine-500 hover:border-blumine-400/60 hover:bg-blumine-50/90 hover:ring-2 hover:ring-blumine-100 hover:scale-105 active:scale-95 transition-all duration-200 group/enhance animate-in fade-in slide-in-from-left-2"
                  title="Enhance with AI"
                >
                  <Sparkles className="w-4 h-4 group-hover/enhance:animate-pulse transition-colors" />
                </button>
              )}

            {enhancingBlockId === block.id &&
              (enhancementSuggestion || isEnhancing) && (
                <div
                  className="absolute left-0 right-0 -top-4 -translate-y-full z-[50] animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-300"
                  style={{ width: "calc(100% + 2rem)", left: "-1rem" }}
                >
                  <div className="mx-auto w-full rounded-[2rem] bg-white border border-white/60 shadow-[0_14px_34px_rgba(16,37,54,0.1),0_2px_8px_rgba(16,37,54,0.05)] backdrop-blur-xl p-2 select-none transition-all">
                    <div className="flex flex-col gap-3">
                      <div className="flex items-center justify-between pl-1">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-blumine-50 text-blumine-500 border border-blumine-100 shadow-inner">
                            <Sparkles className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="text-[11px] font-bold text-zinc-900 uppercase tracking-wider leading-none">
                              AI Suggestion
                            </p>
                            {!isEnhancing && (
                              <p className="text-[10px] text-zinc-500 mt-1 font-medium">
                                Refining your screenplay dialogue
                              </p>
                            )}
                          </div>
                        </div>

                        {!isEnhancing && (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={handleRejectEnhance}
                              className="px-3 py-2 rounded-full text-xs font-bold text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-all active:scale-95"
                            >
                              Discard
                            </button>
                            <button
                              onClick={handleApproveEnhance}
                              className="px-4 py-2 rounded-full text-xs font-bold bg-blumine-500 text-white hover:bg-blumine-600 shadow-lg shadow-blumine-500/20 transition-all active:scale-95 flex items-center gap-1.5"
                            >
                              <Check className="w-3.5 h-3.5" />
                              Apply Changes
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="relative overflow-hidden rounded-[1.25rem] bg-zinc-50/50 border border-zinc-100 p-4 min-h-[4rem]">
                        {isEnhancing ? (
                          <div className="flex flex-col gap-2.5 py-1">
                            <div className="h-3.5 w-3/4 bg-zinc-200/60 animate-pulse rounded-full" />
                            <div className="h-3.5 w-1/2 bg-zinc-200/60 animate-pulse rounded-full" />
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <p className="text-xs text-zinc-400 line-through decoration-zinc-300 italic">
                              {block.text}
                            </p>
                            <p className="text-sm text-zinc-800 font-medium leading-relaxed">
                              {enhancementSuggestion}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3.5 h-3.5 bg-white border-r border-b border-zinc-200/50 rotate-45" />
                </div>
              )}
            {isFocused && phoneticSuggestions.length > 0 && (
              <PhoneticSuggestions
                suggestions={phoneticSuggestions}
                onSelect={handleSelectPhoneticSuggestion}
              />
            )}
          </>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isLocked === nextProps.isLocked &&
      prevProps.isFocused === nextProps.isFocused &&
      prevProps.currentLanguage === nextProps.currentLanguage &&
      prevProps.enhancingBlockId === nextProps.enhancingBlockId &&
      prevProps.isEnhancing === nextProps.isEnhancing &&
      prevProps.enhancementSuggestion === nextProps.enhancementSuggestion &&
      prevProps.block.text === nextProps.block.text &&
      prevProps.block.type === nextProps.block.type &&
      prevProps.block.sceneNumber === nextProps.block.sceneNumber &&
      prevProps.block.sceneType === nextProps.block.sceneType &&
      prevProps.block.timeOfDay === nextProps.block.timeOfDay &&
      (!nextProps.isFocused ||
        (prevProps.showSuggestions === nextProps.showSuggestions &&
          prevProps.showExtensionSuggestions ===
            nextProps.showExtensionSuggestions &&
          prevProps.phoneticSuggestions === nextProps.phoneticSuggestions &&
          prevProps.locations === nextProps.locations &&
          prevProps.characters === nextProps.characters))
    );
  },
);
